import { useEffect, useRef } from 'react';
import { useUserStore } from '../store/user.store';
import { useScannerStore } from '../store/scanner.store';
import { useThemeStore } from '../store/theme.store';
import { useBluetoothStore } from '../store/bluetooth.store';
import { UserSettingsService, UserSettings } from '../services/user-settings.service';
import { useTranslation } from 'react-i18next';

export const useSettingsSync = () => {
    const { user, isAuthenticated } = useUserStore();
    const scanner = useScannerStore();
    const theme = useThemeStore();
    const bt = useBluetoothStore();
    const { i18n } = useTranslation();
    const isInitialMount = useRef(true);
    const lastSynced = useRef<string | null>(null);

    // 1. Initial Load from Cloud
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            console.log('[SettingsSync] 🔄 User changed, fetching cloud settings...');
            UserSettingsService.getSettings(user.id).then(response => {
                if (response.success && response.data) {
                    const data = response.data;
                    console.log('[SettingsSync] ✅ Cloud settings loaded:', data);

                    // Hydrate Scanner Store
                    scanner.setAll({
                        model: data.pda_model as any,
                        scanAction: data.scan_action,
                        scanExtra: data.scan_extra,
                        reprintMode: data.reprint_mode,
                        weightAuditAbs: data.weight_audit_abs,
                        weightAuditPercent: data.weight_audit_percent,
                        exportMode: data.export_mode
                    });

                    // Hydrate Theme
                    if (data.theme_mode) {
                        theme.setMode(data.theme_mode);
                    }

                    // Hydrate Bluetooth
                    if (data.print_size || data.print_protocol) {
                        bt.setBulk({
                            printSize: (data.print_size as any) || bt.printSize,
                            printProtocol: (data.print_protocol as any) || bt.printProtocol
                        });
                    }

                    // Hydrate Language
                    if (data.language && data.language !== i18n.language) {
                        i18n.changeLanguage(data.language);
                    }

                    lastSynced.current = JSON.stringify(data);
                }
            }).finally(() => {
                isInitialMount.current = false;
            });
        }
    }, [user?.id, isAuthenticated]);

    // 2. Sync to Cloud on Changes (Debounced)
    useEffect(() => {
        if (isInitialMount.current || !isAuthenticated || !user?.id) return;

        const currentSettings: UserSettings = {
            pda_model: scanner.model,
            scan_action: scanner.scanAction,
            scan_extra: scanner.scanExtra,
            reprint_mode: scanner.reprintMode,
            weight_audit_abs: scanner.weightAuditAbs,
            weight_audit_percent: scanner.weightAuditPercent,
            export_mode: scanner.exportMode,
            theme_mode: theme.mode,
            language: i18n.language,
            print_size: bt.printSize,
            print_protocol: bt.printProtocol
        };

        const settingsStr = JSON.stringify(currentSettings);
        if (settingsStr === lastSynced.current) return;

        const timer = setTimeout(() => {
            console.log('[SettingsSync] 📤 Syncing settings to cloud...');
            UserSettingsService.saveSettings(user.id, currentSettings).then(res => {
                if (res.success) {
                    lastSynced.current = settingsStr;
                    console.log('[SettingsSync] ✅ Synced');
                }
            });
        }, 2000); // 2 second debounce

        return () => clearTimeout(timer);
    }, [
        scanner.model, scanner.scanAction, scanner.scanExtra, scanner.reprintMode,
        scanner.weightAuditAbs, scanner.weightAuditPercent, scanner.exportMode,
        theme.mode, i18n.language,
        bt.printSize, bt.printProtocol,
        user?.id, isAuthenticated
    ]);
};
